import { HslClassifier } from './classifiers/HslClassifier';
import { HslTextGenerator } from './generators/HslTextGenerator';
import { HslConclusionGenerator } from './generators/HslConclusionGenerator';

export class HslDetailedAnalyzer {
    constructor(language = 'en') {
        this.language = language;
        this.textGenerator = new HslTextGenerator(language);
        this.conclusionGenerator = new HslConclusionGenerator(language);
        this.analysisResults = {
            global: null,
            partitions: {},
            conclusions: {
                global: null,
                partitions: {},
                overall: null
            },
            structuredConclusions: {
                global: null,
                partitions: {}
            }
        };
    }

    setLanguage(language) {
        if (language !== 'zh' && language !== 'en') {
            console.warn('不支持的语言类型，将使用默认中文');
            language = 'zh';
        }

        this.language = language;
        this.textGenerator.setLanguage(language);
        this.conclusionGenerator.setLanguage(language);
    }

    analyzeChannel(channelData, channelName, context = {}) {
        if (!channelData) {
            return {
                statistics: {
                    mean: null,
                    median: null,
                    mode: null,
                    q1: null,
                    q3: null,
                    min: null,
                    max: null,
                    skewness: null,
                    kurtosis: null,
                    variance: null,
                    range: null,
                    stdDev: null,
                    iqr: null
                },
                classification: {}
            };
        }

        const {
            mean,
            median,
            mode,
            q1,
            q3,
            min,
            max,
            skewness,
            kurtosis,
            variance,
            std
        } = channelData;

        const range = max != null && min != null ? max - min : null;
        const stdDev = std != null ? std : (variance != null ? Math.sqrt(variance) : null);
        const iqr = q3 != null && q1 != null ? q3 - q1 : null;

        let classification = {};
        if (channelName === 'l') {
            classification = HslClassifier.classifyLightness(mean, range, skewness, kurtosis);
        } else if (channelName === 's') {
            classification = HslClassifier.classifySaturation(mean, iqr, stdDev, mode, median, skewness);
        } else if (channelName === 'h') {
            classification = HslClassifier.classifyHue(
                { mean, median, mode, iqr },
                context.distribution,
                context.saturationStats
            );
        }

        return {
            statistics: {
                mean,
                median,
                mode,
                q1,
                q3,
                min,
                max,
                skewness,
                kurtosis,
                variance,
                range,
                stdDev,
                iqr
            },
            classification
        };
    }

    generateAnalysisConclusion(analysis, partitionName) {
        const prefix = this.language === 'zh'
            ? (partitionName === 'global' ? '整体' : `${partitionName} 区域`)
            : (partitionName === 'global' ? 'Overall' : `${partitionName} area`);

        if (!analysis.h && !analysis.s && !analysis.l) {
            const skipAnalysis = this.language === 'zh' ? '无有效数据，跳过分析。' : 'No valid data, analysis skipped.';
            return {
                conclusions: {
                    lightness: `${prefix}${this.language === 'zh' ? '明度数据不存在' : ' lightness data not available'}`,
                    hue: `${prefix}${this.language === 'zh' ? '色相数据不存在' : ' hue data not available'}`,
                    saturation: `${prefix}${this.language === 'zh' ? '饱和度数据不存在' : ' saturation data not available'}`,
                    localColor: `${prefix}${this.language === 'zh' ? '局部色彩数据不存在' : ' local color data not available'}`,
                    overallConclusion: `${prefix}${skipAnalysis}`
                },
                structuredConclusions: null
            };
        }

        const structuredConclusions = {
            lightness: {
                level: analysis.l.classification.level,
                dynamicRange: analysis.l.classification.dynamicRange,
                skewness: analysis.l.classification.skewness,
                kurtosis: analysis.l.classification.kurtosis,
                statistics: {
                    mean: analysis.l.statistics.mean,
                    median: analysis.l.statistics.median,
                    min: analysis.l.statistics.min,
                    max: analysis.l.statistics.max,
                    iqr: analysis.l.statistics.iqr,
                    stdDev: analysis.l.statistics.stdDev
                }
            },
            hue: {
                dominantHues: analysis.h.classification.dominantHues || [],
                tone: analysis.h.classification.tone,
                temperature: analysis.h.classification.temperature,
                temperatureRatios: analysis.h.classification.temperatureRatios || {
                    warm: 0,
                    cool: 0,
                    neutral: 0
                },
                distribution: analysis.h.classification.distribution,
                stability: analysis.h.classification.stability,
                isAchromatic: Boolean(analysis.h.classification.isAchromatic),
                rules: {},
                statistics: {
                    mean: analysis.h.statistics.mean,
                    median: analysis.h.statistics.median,
                    min: analysis.h.statistics.min,
                    max: analysis.h.statistics.max,
                    iqr: analysis.h.statistics.iqr,
                    stdDev: analysis.h.statistics.stdDev
                }
            },
            saturation: {
                level: analysis.s.classification.level,
                distribution: analysis.s.classification.distribution,
                stability: analysis.s.classification.stability,
                deviation: analysis.s.classification.deviation,
                rules: {},
                statistics: {
                    mean: analysis.s.statistics.mean,
                    median: analysis.s.statistics.median,
                    min: analysis.s.statistics.min,
                    max: analysis.s.statistics.max,
                    iqr: analysis.s.statistics.iqr,
                    stdDev: analysis.s.statistics.stdDev
                }
            },
            localColor: {
                ...HslClassifier.classifyLocalColor(analysis.s.statistics, analysis.l.statistics)
            }
        };

        const textConclusions = this.textGenerator.generateTextDescriptions(structuredConclusions);

        return {
            conclusions: {
                lightness: structuredConclusions.lightness,
                hue: structuredConclusions.hue,
                saturation: structuredConclusions.saturation,
                localColor: structuredConclusions.localColor,
                overallConclusion: textConclusions.overallConclusion
            },
            structuredConclusions
        };
    }

    analyzePartition(partitionData, partitionName, distribution) {
        if (!partitionData || (!partitionData.h && !partitionData.s && !partitionData.l)) {
            console.log(`[HslDetailedAnalyzer] ${partitionName} 分区数据为空或所有通道为空，返回空数据结构`);
            return {
                name: partitionName,
                h: this.analyzeChannel(null, 'h'),
                s: this.analyzeChannel(null, 's'),
                l: this.analyzeChannel(null, 'l'),
                conclusions: {
                    lightness: `${partitionName} 区域无数据`,
                    hue: `${partitionName} 区域无数据`,
                    saturation: `${partitionName} 区域无数据`,
                    localColor: `${partitionName} 区域无数据`,
                    overallConclusion: `${partitionName} 区域无有效数据，无需分析。`
                },
                structuredConclusions: {
                    lightness: {
                        level: null,
                        dynamicRange: null
                    },
                    hue: {
                        dominantHues: [],
                        tone: null,
                        temperature: null,
                        temperatureRatios: {
                            warm: 0,
                            cool: 0,
                            neutral: 0
                        },
                        distribution: null,
                        stability: null,
                        isAchromatic: false
                    },
                    saturation: {
                        level: null,
                        distribution: null,
                        stability: null,
                        deviation: null
                    },
                    localColor: {
                        highlights: null,
                        lowlights: null,
                        balance: null
                    }
                }
            };
        }

        const l = this.analyzeChannel(partitionData.l, 'l');
        const s = this.analyzeChannel(partitionData.s, 's');
        const h = this.analyzeChannel(partitionData.h, 'h', {
            distribution,
            saturationStats: s.statistics
        });

        const hueDistribution = distribution?.h;
        const hueProcessMetrics = Array.isArray(hueDistribution) && hueDistribution.length > 0
            ? (() => {
                const buckets = HslClassifier.aggregateHueBuckets(hueDistribution);
                const { entropy, resultantLength, peakCount } = HslClassifier.computeHueDistributionStats(buckets);
                const modeMean = HslClassifier.circularDistance(h?.statistics?.mode, h?.statistics?.mean);
                const modeMedian = HslClassifier.circularDistance(h?.statistics?.mode, h?.statistics?.median);
                return {
                    entropy: Number(entropy.toFixed(4)),
                    resultantLength: Number(resultantLength.toFixed(4)),
                    peakCount,
                    modeMean: modeMean == null ? null : Number(modeMean.toFixed(2)),
                    modeMedian: modeMedian == null ? null : Number(modeMedian.toFixed(2))
                };
            })()
            : null;
        const dominantHueProcess = Array.isArray(hueDistribution) && hueDistribution.length > 0
            ? (() => {
                const buckets = HslClassifier.aggregateHueBuckets(hueDistribution);
                const total = buckets.reduce((sum, value) => sum + Math.max(0, Number(value) || 0), 0);
                if (total <= 0) {
                    return {
                        total: 0,
                        ratios: [],
                        significantThreshold: 0.08,
                        minCoverage: 0.65,
                        maxCount: 3,
                        significantIndices: [],
                        selectedIndices: [],
                        coverage: 0
                    };
                }
                const ratios = buckets.map((value, index) => ({
                    tone: HslClassifier.getHueToneByIndex(index),
                    ratio: Number((Math.max(0, Number(value) || 0) / total).toFixed(4))
                }));
                const options = HslClassifier.getDominantHueOptions();
                const sorted = ratios
                    .map((item, index) => ({ index, ratio: item.ratio }))
                    .sort((a, b) => b.ratio - a.ratio);
                const significant = sorted.filter((bucket) => bucket.ratio >= options.significantThreshold);
                const selected = (significant.length > 0 ? significant : [sorted[0]]).slice(0, options.maxCount);
                let coverage = selected.reduce((sum, bucket) => sum + bucket.ratio, 0);
                if (significant.length > 0 && coverage < options.minCoverage) {
                    sorted.forEach((bucket) => {
                        if (coverage >= options.minCoverage || selected.length >= options.maxCount) return;
                        if (!selected.some((item) => item.index === bucket.index)) {
                            selected.push(bucket);
                            coverage += bucket.ratio;
                        }
                    });
                }
                return {
                    total: Number(total.toFixed(2)),
                    ratios,
                    significantThreshold: options.significantThreshold,
                    minCoverage: options.minCoverage,
                    maxCount: options.maxCount,
                    significantIndices: significant.map((item) => item.index),
                    selectedIndices: selected.map((item) => item.index),
                    coverage: Number(coverage.toFixed(4))
                };
            })()
            : null;
        console.log('[HslDetailedAnalyzer] dominantHues', {
            partition: partitionName,
            dominantHues: h?.classification?.dominantHues || [],
            isAchromatic: Boolean(h?.classification?.isAchromatic),
            distribution: h?.classification?.distribution || null,
            stability: h?.classification?.stability || null,
            process: hueProcessMetrics,
            dominantProcess: dominantHueProcess
        });

        const analysis = { h, s, l };
        const analysisConclusion = this.generateAnalysisConclusion(analysis, partitionName);

        return {
            name: partitionName,
            ...analysis,
            conclusions: analysisConclusion.conclusions,
            structuredConclusions: analysisConclusion.structuredConclusions
        };
    }

    analyze(hslData) {
        if (!hslData) {
            console.warn('HslDetailedAnalyzer: 输入数据为空');
            return null;
        }

        try {
            const globalResult = this.analyzePartition(hslData.global, 'global', hslData.distribution);
            this.analysisResults.global = globalResult;

            if (globalResult.conclusions && globalResult.conclusions.overallConclusion) {
                this.analysisResults.conclusions.global = globalResult.conclusions;
                this.analysisResults.structuredConclusions.global = globalResult.structuredConclusions;
            }

            ['blacks', 'shadows', 'midtones', 'highlights', 'whites'].forEach((partition) => {
                const partitionDistribution = hslData.partitionDistributions?.[partition];
                const result = this.analyzePartition(
                    hslData[partition],
                    partition,
                    partitionDistribution
                );
                this.analysisResults.partitions[partition] = result;

                if (result.conclusions && result.conclusions.overallConclusion) {
                    this.analysisResults.conclusions.partitions[partition] = result.conclusions;
                    this.analysisResults.structuredConclusions.partitions[partition] = result.structuredConclusions;
                }
            });

            if (!this.analysisResults.global) {
                console.warn('HslDetailedAnalyzer: 全局分析数据无效');
                return null;
            }

            if (this.analysisResults.global && this.analysisResults.partitions) {
                this.analysisResults.conclusions.overall =
                    this.conclusionGenerator.generateOverallConclusion(
                        this.analysisResults.structuredConclusions.global,
                        this.analysisResults.structuredConclusions.partitions
                    );
            }

            return this.analysisResults;
        } catch (error) {
            console.error('HslDetailedAnalyzer: 分析过程出错:', error);
            return null;
        }
    }
}
